import { useState, useEffect, useRef, useCallback, useMemo, forwardRef, memo } from 'react'
import { formatDistanceToNow, isToday, isYesterday, subDays, isAfter } from 'date-fns'
import {
  Search,
  FileText,
  FilePlus,
  ArrowUp,
  ArrowDown,
  CornerDownLeft,
  Pin,
  PinOff,
  X,
} from 'lucide-react'
import { useSearchNotes, getDisplayTitle, togglePinNote } from '../hooks/useNotes'
import { isContentEmpty } from '../db/sync'
import { track, AnalyticsEvents } from '../lib/mixpanel'
import type { Note } from '../db/dexie'

// --- Action definitions ---

interface PaletteAction {
  id: string
  label: string
  description: string
  icon: React.ReactNode
  keywords: string[]
}

function buildActions(): PaletteAction[] {
  return [
    {
      id: 'new-note',
      label: 'New Note',
      description: 'Create a new note',
      icon: <FilePlus className="w-4 h-4" />,
      keywords: ['create', 'add', 'new', 'note'],
    },
  ]
}

// --- Types for unified list items ---

type PaletteItem =
  | { type: 'action'; action: PaletteAction }
  | { type: 'header'; label: string }
  | { type: 'note'; note: Note }

// --- Component ---

interface CommandPaletteProps {
  isOpen: boolean
  onClose: () => void
  onSelectNote: (noteId: string) => void
  onNewNote: () => void
  onToggleTheme: () => void
  onDeleteNote: (noteId: string) => void
  onPinToggle?: (noteId: string) => void
}

export function CommandPalette({
  isOpen,
  onClose,
  onSelectNote,
  onNewNote,
  onDeleteNote,
  onPinToggle,
}: CommandPaletteProps) {
  const [query, setQuery] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLUListElement>(null)
  const itemRefs = useRef<Map<number, HTMLLIElement>>(new Map())
  const allNotes = useSearchNotes(query)
  // Filter out empty notes — e.g. the blank note created when a new tab opens
  const notes = useMemo(() => allNotes.filter(n => !isContentEmpty(n.content)), [allNotes])

  const actions = useMemo(() => buildActions(), [])

  // Filter actions when there's a query
  const filteredActions = useMemo(() => {
    if (!query.trim()) return actions
    const q = query.toLowerCase()
    return actions.filter(
      a =>
        a.label.toLowerCase().includes(q) ||
        a.description.toLowerCase().includes(q) ||
        a.keywords.some(k => k.includes(q))
    )
  }, [actions, query])

  // Group notes logic
  const groupedItems = useMemo(() => {
    const items: PaletteItem[] = []

    // 1. Actions
    if (filteredActions.length > 0) {
      items.push({ type: 'header', label: 'Actions' })
      filteredActions.forEach(action => items.push({ type: 'action', action }))
    }

    if (notes.length === 0) return items

    // 2. Pinned Notes
    const pinnedNotes = notes.filter(n => n.is_pinned)
    const unpinnedNotes = notes.filter(n => !n.is_pinned)

    if (pinnedNotes.length > 0) {
      items.push({ type: 'header', label: 'Pinned' })
      pinnedNotes.forEach(note => items.push({ type: 'note', note }))
    }

    // 3. Chronological Groups for Unpinned Notes
    const today: Note[] = []
    const yesterday: Note[] = []
    const previous7Days: Note[] = []
    const older: Note[] = []

    const now = new Date()
    const sevenDaysAgo = subDays(now, 7)

    unpinnedNotes.forEach(note => {
      const noteDate = new Date(note.updated_at)
      if (isToday(noteDate)) {
        today.push(note)
      } else if (isYesterday(noteDate)) {
        yesterday.push(note)
      } else if (isAfter(noteDate, sevenDaysAgo)) {
        previous7Days.push(note)
      } else {
        older.push(note)
      }
    })

    if (today.length > 0) {
      items.push({ type: 'header', label: 'Today' })
      today.forEach(note => items.push({ type: 'note', note }))
    }
    if (yesterday.length > 0) {
      items.push({ type: 'header', label: 'Yesterday' })
      yesterday.forEach(note => items.push({ type: 'note', note }))
    }
    if (previous7Days.length > 0) {
      items.push({ type: 'header', label: 'Previous 7 Days' })
      previous7Days.forEach(note => items.push({ type: 'note', note }))
    }
    if (older.length > 0) {
      items.push({ type: 'header', label: 'Older' })
      older.forEach(note => items.push({ type: 'note', note }))
    }

    return items
  }, [filteredActions, notes])

  // Specific list of selectable items (skipping headers)
  const selectableItems = useMemo(() => {
    return groupedItems.filter(item => item.type !== 'header') as (
      | { type: 'action'; action: PaletteAction }
      | { type: 'note'; note: Note }
    )[]
  }, [groupedItems])

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen) {
      setQuery('') // eslint-disable-line react-hooks/set-state-in-effect -- intentional reset on open
      setSelectedIndex(0)
      setTimeout(() => inputRef.current?.focus(), 50)
    }
  }, [isOpen])

  // Reset selection when items change
  useEffect(() => {
    setSelectedIndex(0) // eslint-disable-line react-hooks/set-state-in-effect -- intentional reset on data change
  }, [selectableItems.length])

  // Scroll selected item into view
  useEffect(() => {
    if (selectedIndex === 0 && listRef.current) {
      // Scroll to top so the first item and its section header are fully visible
      listRef.current.scrollTop = 0
      return
    }
    const el = itemRefs.current.get(selectedIndex)
    if (el) {
      el.scrollIntoView({ block: 'nearest' })
    }
  }, [selectedIndex])

  // --- Execute an action ---
  const executeAction = useCallback(
    (actionId: string) => {
      track(AnalyticsEvents.COMMAND_PALETTE_ACTION_EXECUTED, { action: actionId })
      if (actionId === 'new-note') {
        onNewNote()
        onClose()
      }
    },
    [onNewNote, onClose]
  )

  // Delete single note from palette
  const handleDeleteNote = useCallback((e: React.MouseEvent, noteId: string) => {
    e.stopPropagation()
    onDeleteNote(noteId)
  }, [onDeleteNote])

  // --- Select the currently highlighted item ---
  const selectCurrent = useCallback(() => {
    const item = selectableItems[selectedIndex]
    if (!item) return

    if (item.type === 'action') {
      executeAction(item.action.id)
    } else {
      onSelectNote(item.note.id)
      onClose()
    }
  }, [selectableItems, selectedIndex, executeAction, onSelectNote, onClose])

  // --- Toggle Pin ---
  const handleTogglePin = useCallback(async (e: React.MouseEvent, noteId: string) => {
    e.stopPropagation()
    const note = notes.find(n => n.id === noteId)
    await togglePinNote(noteId)
    track(note?.is_pinned ? AnalyticsEvents.NOTE_UNPINNED : AnalyticsEvents.NOTE_PINNED, { source: 'command_palette' })
    onPinToggle?.(noteId)
  }, [notes, onPinToggle])

  // --- Keyboard navigation ---
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setSelectedIndex(i => Math.min(i + 1, selectableItems.length - 1))
          break
        case 'ArrowUp':
          e.preventDefault()
          setSelectedIndex(i => Math.max(i - 1, 0))
          break
        case 'Enter':
          e.preventDefault()
          selectCurrent()
          break
        case 'Escape':
          e.preventDefault()
          onClose()
          break
      }
    },
    [selectableItems.length, selectCurrent, onClose]
  )

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center dialog-backdrop"
      style={{ paddingTop: '18vh' }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-xl mx-3 sm:mx-0 rounded-2xl overflow-hidden shadow-2xl"
        style={{ backgroundColor: 'hsl(var(--color-bg-elevated))' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Search Input */}
        <div
          className="flex items-center gap-3 px-4 sm:px-5 py-3 sm:py-4 border-b"
          style={{ borderColor: 'hsl(var(--color-border))' }}
        >
          <Search
            className="w-5 h-5 flex-shrink-0"
            style={{ color: 'hsl(var(--color-text-muted))' }}
          />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a command or search notes..."
            className="flex-1 bg-transparent text-sm sm:text-base outline-none"
            style={{ color: 'hsl(var(--color-text-primary))' }}
          />
        </div>


        {/* Results */}
        <ul ref={listRef} className="max-h-80 overflow-y-auto" style={{ scrollPaddingTop: '2rem' }}>
          {groupedItems.length === 0 ? (
            <li
              className="px-5 py-8 text-center"
              style={{ color: 'hsl(var(--color-text-secondary))' }}
            >
              <p>No results found</p>
            </li>
          ) : (
            groupedItems.map((item) => {
              if (item.type === 'header') {
                return <SectionLabel key={`header-${item.label}`} label={item.label} />
              }

              // Calculate index within selectable items for highlighting
              const selectableIndex = selectableItems.indexOf(item)
              const isSelected = selectedIndex === selectableIndex

              if (item.type === 'action') {
                return (
                  <ActionItem
                    key={item.action.id}
                    ref={(el) => { if (el) itemRefs.current.set(selectableIndex, el); else itemRefs.current.delete(selectableIndex); }}
                    action={item.action}
                    isSelected={isSelected}
                    onClick={() => executeAction(item.action.id)}
                  />
                )
              }

              return (
                <NoteItem
                  key={item.note.id}
                  ref={(el) => { if (el) itemRefs.current.set(selectableIndex, el); else itemRefs.current.delete(selectableIndex); }}
                  note={item.note}
                  isSelected={isSelected}
                  onClick={() => {
                    onSelectNote(item.note.id)
                    onClose()
                  }}
                  onTogglePin={(e) => handleTogglePin(e, item.note.id)}
                  onDelete={(e) => handleDeleteNote(e, item.note.id)}
                />
              )
            })
          )}
        </ul>

        {/* Keyboard Hints — hidden on mobile */}
        <div
          className="hidden sm:flex px-5 py-3 border-t items-center gap-6"
          style={{
            borderColor: 'hsl(var(--color-border))',
            backgroundColor: 'hsl(var(--color-bg-secondary))',
          }}
        >
          <KeyHint icon={<ArrowUp className="w-3 h-3" />} label="Up" />
          <KeyHint icon={<ArrowDown className="w-3 h-3" />} label="Down" />
          <KeyHint icon={<CornerDownLeft className="w-3 h-3" />} label="Select" />
          <KeyHint label="Esc" labelOnly text="Close" />
        </div>
      </div>
    </div>
  )
}

// --- Sub-components ---

function SectionLabel({ label }: { label: string }) {
  return (
    <li
      className="px-5 pt-3 pb-1 text-xs font-semibold uppercase tracking-wider sticky top-0 bg-opacity-95 backdrop-blur-sm z-10"
      style={{
        color: 'hsl(var(--color-text-muted))',
        backgroundColor: 'hsl(var(--color-bg-elevated))'
      }}
    >
      {label}
    </li>
  )
}

const ActionItem = memo(forwardRef<HTMLLIElement, {
  action: PaletteAction
  isSelected: boolean
  onClick: () => void
}>(({ action, isSelected, onClick }, ref) => {
  return (
    <li
      ref={ref}
      className="px-5 py-3 cursor-pointer flex items-center gap-3 transition-fast"
      style={{
        backgroundColor: isSelected ? 'hsl(var(--color-bg-tertiary))' : 'transparent',
      }}
      onClick={onClick}
    >
      <span style={{ color: 'hsl(var(--color-accent))' }}>{action.icon}</span>
      <div className="flex-1 min-w-0">
        <span
          className="font-medium block"
          style={{ color: 'hsl(var(--color-text-primary))' }}
        >
          {action.label}
        </span>
        <span
          className="text-xs block"
          style={{ color: 'hsl(var(--color-text-muted))' }}
        >
          {action.description}
        </span>
      </div>
    </li>
  )
}))

const NoteItem = memo(forwardRef<HTMLLIElement, {
  note: Note
  isSelected: boolean
  onClick: () => void
  onTogglePin: (e: React.MouseEvent) => void
  onDelete: (e: React.MouseEvent) => void
}>(({ note, isSelected, onClick, onTogglePin, onDelete }, ref) => {
  const title = getDisplayTitle(note)
  const timeAgo = formatDistanceToNow(new Date(note.updated_at), { addSuffix: true })

  return (
    <li
      ref={ref}
      className="px-4 sm:px-5 py-2.5 sm:py-3 cursor-pointer flex items-center gap-3 transition-fast group"
      style={{
        backgroundColor: isSelected ? 'hsl(var(--color-bg-tertiary))' : 'transparent',
      }}
      onClick={onClick}
    >
      <FileText
        className="w-4 h-4 flex-shrink-0 hidden sm:block"
        style={{ color: 'hsl(var(--color-accent))' }}
      />
      <div className="flex-1 min-w-0">
        <span
          className="font-medium truncate block"
          style={{ color: 'hsl(var(--color-text-primary))' }}
        >
          {title}
        </span>
        <span
          className="text-xs flex-shrink-0"
          style={{ color: 'hsl(var(--color-text-muted))' }}
        >
          {timeAgo}
        </span>
      </div>

      {/* Pin Button - always visible on mobile, hover on desktop */}
      <button
        onClick={onTogglePin}
        className={`p-1.5 rounded-md transition-fast ${note.is_pinned ? 'opacity-100' : 'sm:opacity-0 sm:group-hover:opacity-100 opacity-100'}`}
        style={{
          color: note.is_pinned ? 'hsl(var(--color-accent))' : 'hsl(var(--color-text-muted))',
          backgroundColor: note.is_pinned ? 'hsl(var(--color-bg-tertiary))' : 'transparent'
        }}
        title={note.is_pinned ? 'Unpin note' : 'Pin note'}
      >
        {note.is_pinned ? (
          <PinOff className="w-3.5 h-3.5" />
        ) : (
          <Pin className="w-3.5 h-3.5" />
        )}
      </button>

      {/* Delete Button - always visible on mobile, hover on desktop */}
      <button
        onClick={onDelete}
        className="p-1.5 rounded-md transition-fast sm:opacity-0 sm:group-hover:opacity-100 opacity-100 hover:!opacity-100"
        style={{
          color: 'hsl(var(--color-error, 0 84% 60%))',
        }}
        title="Delete note"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </li>
  )
}))

function KeyHint({
  icon,
  label,
  labelOnly,
  text,
}: {
  icon?: React.ReactNode
  label: string
  labelOnly?: boolean
  text?: string
}) {
  return (
    <div
      className="flex items-center gap-1.5 text-xs"
      style={{ color: 'hsl(var(--color-text-muted))' }}
    >
      <kbd
        className="px-1.5 py-0.5 rounded flex items-center gap-1"
        style={{ backgroundColor: 'hsl(var(--color-bg-tertiary))' }}
      >
        {icon}
        {labelOnly && label}
      </kbd>
      {!labelOnly && <span>{label}</span>}
      {text && <span>{text}</span>}
    </div>
  )
}
